import React, { useEffect, useState } from 'react';
import { CSVLink } from 'react-csv';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useAppDispatch } from '../../../../app/hooks';
import { tryToFetchAllTransactions } from '../../../../store/brokers/partners/transactions/transactionsSlice';
import TransactionsTable from '../tables/TransactionsTable';
import Button from '../../../../shared/button';
import Modal from '../../../../shared/modal';
import Error from '../../../../shared/error';

interface IExportModal {
    openModal: boolean;
    data?: any;
    columns?: any;
    extraData?: any;
    handleCloseModal: () => void;
    onGettingFilters: (download: boolean) => void;
}

const ExportModal = ({
    openModal,
    data,
    columns,
    extraData,
    handleCloseModal,
    onGettingFilters
}: IExportModal) => {
    const dispatch = useAppDispatch();
    const csvLink = React.useRef() as React.MutableRefObject<any>;
    const [loadingDownloadCSV, setLoadingDownloadCSV] = useState<boolean>(false)
    const [loadingDownloadExcel, setLoadingDownloadExcel] = useState<boolean>(false)
    const [downloadCSV, setDownloadCSV] = useState<any>()
    const [submitError, setSubmitError] = useState<any>();

    const onCloseModal = () => {
        handleCloseModal();
    };

    const exportToExcel = (data: string[][], fileName: string = 'data.xlsx') => {
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        saveAs(blob, fileName);
        setLoadingDownloadExcel(false)
    };

    const onDownloadData = async (type: string) => {
        if (type === 'csv') setLoadingDownloadCSV(true)
        if (type === 'excel') setLoadingDownloadExcel(true)
        try {
            setSubmitError(undefined)
            const filters: any = onGettingFilters(true)
            const formatColumns: any = []
            columns && columns?.length > 0 && columns?.map((item: any) => {
                formatColumns.push(item?.key)
                if (item?.secondKey) {
                    formatColumns?.push(item?.secondKey)
                }
                return item
            })
            const formatBody = {
                columns: formatColumns,
                ...filters?.data || {},
            }
            const response: any = await dispatch(tryToFetchAllTransactions(formatBody)).unwrap()
            if (type === 'excel') {
                exportToExcel(response, 'Transactions-data.xlsx')
            } else {
                setDownloadCSV(response || [])
            }
        } catch (err) {
            setSubmitError(`${err}`)
            setLoadingDownloadCSV(false)
            setLoadingDownloadExcel(false)
        }
    }

    useEffect(() => {
        if (downloadCSV && (loadingDownloadCSV)) {
            csvLink.current.link.click()
        }
        setLoadingDownloadCSV(false)
        setLoadingDownloadExcel(false)
    }, [downloadCSV])

    const formatNumber = (value: number | string | null | undefined): any => {
        if (value === null || value === undefined || value === '') return 0;

        const num = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(num)) return '';

        return num.toLocaleString('en-US');
    };

    return (
        <Modal
            open={openModal}
            title='Export Data'
            headerStyle='border-b-1 py-3 mb-4'
            onClose={onCloseModal}
            contentContainerStyle='!rounded-xl min-w-[95%] md:min-w-[70%] lg:min-w-[60%]'>
            <div className='p-2 max-w-[95vw] md:min-w-[70%] lg:min-w-[60%] min-h-[500px]'>
                <div className='flex flex-col md:flex-row md:justify-between my-3'>
                    <p className='text-primary-light dark:text-primary-dark mb-3'>How do you want your data exported?</p>
                    <div className='flex flex-col md:flex-row'>
                        <Button
                            label={loadingDownloadExcel ? 'Preparing data' : 'Download as .xlxs'}
                            className={`${!loadingDownloadExcel ? 'btn-primary-rounded-less dark:!bg-black' : 'btn-primary-rounded-less-disable !cursor-not-allowed dark:!bg-gray-500'} md:mr-3 mb-4 md:mb-0`}
                            onClickButton={() => !loadingDownloadExcel && onDownloadData('excel')}
                        />
                        <CSVLink
                            ref={csvLink}
                            data={downloadCSV?.filter((item: any, idx: number, arr: any) => { return idx > 0 }) || []}
                            headers={downloadCSV?.[0] || []}
                            className={'none'}
                            target={'_blank'}
                            filename={`Transactions-data.csv`}
                        ></CSVLink>
                        <Button
                            label={loadingDownloadCSV ? 'Preparing data' : 'Download as .csv'}
                            className={`${!loadingDownloadCSV ? 'btn-primary-rounded-less dark:!bg-black' : 'btn-primary-rounded-less-disable !cursor-not-allowed dark:!bg-gray-500'} md:mr-3`}
                            onClickButton={() => !loadingDownloadCSV && onDownloadData('csv')}
                        />
                    </div>
                </div>
                {submitError && <Error text={submitError} />}
                <TransactionsTable
                    rows={data}
                    columns={columns}
                    hideActions={true}
                />
                <div className='flex flex-row justify-start items-center my-5'>
                    <p className='mr-4 font-bold dark:text-primary-dark'>{extraData?.totalTransactions} Total Transactions</p>
                    <p className='mr-4 font-bold text-[#EF4444]'>{formatNumber(extraData?.unmatchedTransactions || 0)} Total Unmatched Transaction</p>
                    <p className='mr-4 font-bold  text-[#EF4444]'>€{formatNumber(extraData?.totalUnmatchedamount || 0)} Total Unmatched Amount</p>
                </div>
            </div>
        </Modal>
    );
};

export default ExportModal;